import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import userService from "../../services/UserService";
import authService from "../../services/AuthService";

interface LoginPayload {
  email: string;
  password: string;
}

interface Group {
  id: number;
  title: string;
  permissions: string[];
}
interface User {
  id: number;
  first_name: string;
  last_name: string;
  active: boolean;
  email: string;
  username: string;
  group: Group;
  user_group_id: number;
  timezone: string;
}

interface Register {
  email: string;
  password: string;
  username: string;
  first_name: string;
  last_name: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isInit: boolean;
  error: string | null;
  loading: boolean;
  notification: string | null;
  rehydrated: boolean;
}

const initialState: AuthState = {
  user: null,
  isInit: true,
  isAuthenticated: false,
  error: null,
  loading: false,
  notification: null,
  rehydrated: false,
};

export const login = createAsyncThunk(
  "auth/login",
  async ({ email, password }: LoginPayload, { rejectWithValue }) => {
    try {
      const response = await authService.login(email, password);
      return response.data;
    } catch (error) {
      console.log(error)
      return rejectWithValue(
        error?.response?.data?.message || "Login failed. Please check your credentials."
      );
    }
  }
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.logout();
      return response.data;
    } catch (error) {
      return rejectWithValue("Logout failed.");
    }
  }
);

export const register = createAsyncThunk(
  "auth/register",
  async (user: Register, { rejectWithValue }) => {
    try {
      const response = await authService.register(user);
      return response.data;
    } catch (error) {
      return rejectWithValue("Failed to register user.");
    }
  }
);

export const userInfo = createAsyncThunk(
  "auth/user",
  async (_, { rejectWithValue }) => {
    try {
      const response = await userService.getInfo();
      return response.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch user info.");
    }
  }
);

export const rehydrateAuthState = createAsyncThunk(
  "auth/rehydrate",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const response = await userService.getInfo();
        return { user: response.data };
      } else {
        return rejectWithValue("No tokens found");
      }
    } catch (error) {
      return rejectWithValue("Failed to rehydrate auth state.");
    }
  }
);

export const refreshToken = createAsyncThunk(
  "auth/refresh",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.refresh();
      return response.data;
    } catch (error) {
      return rejectWithValue("Failed to refresh token.");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, action) => {

        if (!localStorage.getItem("userTimezone")) {
          localStorage.setItem("userTimezone", action.payload.user.timezone);
        }

        state.isAuthenticated = true;
        state.error = null;
        state.loading = false;
        state.notification = "Login successful!";
        localStorage.setItem("token", action.payload.access_token);
        localStorage.setItem("refreshToken", action.payload.refresh_token);
      })
      .addCase(login.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
        state.notification = action.payload as string;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.isInit = false;
        state.notification = "Logout successful!";
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
      })
      .addCase(logout.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
        state.notification = action.payload as string;
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        state.isAuthenticated = false;
        state.user = null;
      })
      .addCase(userInfo.fulfilled, (state, action) => {
        state.user = action.payload;
        state.loading = false;
        state.rehydrated = false;
      })
      .addCase(userInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(userInfo.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
        state.notification = action.payload as string;
        state.user = null;
        state.rehydrated = false;
      })
      .addCase(rehydrateAuthState.pending, (state, action) => {
        state.isInit = true;
      })
      .addCase(rehydrateAuthState.fulfilled, (state, action) => {
        state.isInit = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.rehydrated = true;
        if (!localStorage.getItem("userTimezone")) {
          localStorage.setItem("userTimezone", action.payload.user.timezone);
        }

      })
      .addCase(rehydrateAuthState.rejected, (state) => {
        state.isInit = false;
        state.isAuthenticated = false;
        state.user = null;
        state.rehydrated = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        localStorage.setItem("token", action.payload.access_token);
        localStorage.setItem("refreshToken", action.payload.refresh_token);
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.error = action.payload as string;
        state.notification = action.payload as string;
        state.isAuthenticated = false;
        state.user = null;
      });
  },
});

const { reducer } = authSlice;
export default reducer;
